/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useTacacsData } from "./hooks";
import { validateRadius } from "./helpers";
import { isError } from "../../../../utils/validations";
import { useSelector } from "react-redux";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { setErrorMsg } from "../../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../../store/hooks";
import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { PasswordField } from "../../../Shared/Radix/Inputs/PasswordField";
import { Label } from "../../../ui/label";
import { Checkbox } from "../../../ui/checkbox";
import { Button } from "../../../ui/button";
import { ShadcnHeading } from "../../../ui/shadcn-heading";

const Tacacs: React.FC = () => {
  const { tacacsForm, setTacacsForm, errMsg, setErrMsg, submitHandler, cancelHandler, serverAddress, sharedSecret, confirmSharedSecret, port, profileAttributeName } = useTacacsData();
  const dispatch = useStoreDispatch();
  const storeErrorMsg = useSelector(errorMsgSelector);

  /* Adapter: Radix InputField/PasswordField onChange(name, value) → hook state */
  const handleChange = (name: string, value: string) => {
    setTacacsForm({ ...tacacsForm, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
    dispatch(setErrorMsg({ ...storeErrorMsg, [name]: "" }));
  };

  const handleOnCheck = (checked: boolean) => {
    if (checked) {
      setTacacsForm({ ...tacacsForm, isChangeSharedSecret: checked });
      setErrMsg({ ...errMsg, sharedSecret: "", confirmSharedSecret: "" });
    } else {
      setTacacsForm({ ...tacacsForm, sharedSecret: "", confirmSharedSecret: "", isChangeSharedSecret: checked });
      setErrMsg({ ...errMsg, sharedSecret: "", confirmSharedSecret: "" });
    }
  };

  const blurHandler = () => {
    const errorMessages = validateRadius(tacacsForm);
    setErrMsg({ ...errMsg, ...errorMessages });
    dispatch(setErrorMsg({ ...storeErrorMsg, ...errorMessages }));
  };

  const isValidFormData = tacacsForm?.isChangeSharedSecret
    ? !!tacacsForm.serverAddress &&
    !!tacacsForm.sharedSecret &&
    !!tacacsForm.confirmSharedSecret &&
    !!tacacsForm.port &&
    !!tacacsForm.profileAttributeName
    : !!tacacsForm.serverAddress && !!tacacsForm.port && !!tacacsForm.profileAttributeName;
  const isDisableSave = isValidFormData && !isError(errMsg);

  return (

    <div
      className="flex flex-col w-full overflow-y-auto px-1 md:px-1"
      style={{ color: "var(--foreground)" }}
    >
      {/* Heading */}
     <h2 style={{ margin: 0, padding: 0, lineHeight: 0.5, marginBottom: "10px" }}>
        <ShadcnHeading className="mb-0 text-sm font-semibold text-foreground" as="span">
          TACACS+ Settings
        </ShadcnHeading>
      </h2>

        <div className="mt-3"
        style={{
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius)",
          padding: "24px",
          boxShadow: "var(--shadow-card)",
          backgroundColor: "var(--background)",
        }}
      >
        <form onSubmit={submitHandler} autoComplete="off">
          <div className="space-y-5 max-w-lg">
            {/* Server Address */}
            <InputField
              required
              label="Server Address"
              name={serverAddress}
              value={tacacsForm?.serverAddress}
              maxLength={255}
              placeholder="Enter server address"
              onChange={handleChange}
              onBlur={blurHandler}
              tooltipText="Maximum length: 255 characters"
            />


            {/* Change Shared Secret checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="isChangeSharedSecret"
                checked={tacacsForm?.isChangeSharedSecret}
                onCheckedChange={(checked) => handleOnCheck(!!checked)}
                className="m-0"
              />
              <Label htmlFor={"isChangeSharedSecret"} className="text-sm font-medium">
                             Change Shared Secret
                           </Label>

            </div>

            {/* Shared Secret */}
            <PasswordField
              label="Shared Secret"
              name={sharedSecret}
              value={tacacsForm?.sharedSecret ?? ""}
              disabled={!tacacsForm.isChangeSharedSecret}
              onChange={handleChange}
              placeholder="Enter shared secret"
              onBlur={blurHandler}
            />


            {/* Confirm Shared Secret */}
            <PasswordField
              label="Confirm Shared Secret"
              name={confirmSharedSecret}
              value={tacacsForm?.confirmSharedSecret ?? ""}
              disabled={!tacacsForm.isChangeSharedSecret}
              onChange={handleChange}
              placeholder="Confirm shared secret"
              onBlur={blurHandler}
            />

            {/* Port */}
            <InputField
              required
              label="Port"
              name={port}
              type="number"
              value={tacacsForm?.port}
              min={1}
              max={65535}
              placeholder="Enter port number"
              onChange={handleChange}
              onBlur={blurHandler}
            />


            {/* Profile Attribute Name */}
            <InputField
              required
              label="Profile Attribute Name"
              name={profileAttributeName}
              value={tacacsForm?.profileAttributeName}
              maxLength={2048}
              placeholder="Enter attribute name"
              onChange={handleChange}
              onBlur={blurHandler}
              tooltipText="Maximum length: 2048 characters"
              pattern={/^[a-zA-Z0-9]*$/}
              patternMsg="Only alphanumeric characters allowed"
            />

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" type="button" onClick={cancelHandler}>
                Cancel
              </Button>
              <Button type="submit" disabled={!isDisableSave}>
                Save
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Tacacs;
